import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Edit3, Save, RotateCcw, Eye, ChevronRight, RefreshCw, History, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";

const CATEGORY_COLOR = {
  Clinical:        { bg: "#FEE2E2", text: "#991B1B" },
  Patient:         { bg: "#E0E7FF", text: "#3730A3" },
  "User Lifecycle":{ bg: "#FEF3C7", text: "#92400E" },
  License:         { bg: "#F3E8FF", text: "#6B21A8" },
};

function SegmentBadge({ length, segments, per_segment }) {
  const isMulti = segments > 1;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase font-bold"
      style={{
        background: isMulti ? "#FEF3C7" : "#D1FAE5",
        color: isMulti ? "#92400E" : "#065F46",
      }}
      title={`${length} chars · ${per_segment} chars per segment`}
    >
      {length} chars · {segments} seg
    </span>
  );
}

export default function SmsTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/sms/templates");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byCategory = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      if (!m.has(it.category)) m.set(it.category, []);
      m.get(it.category).push(it);
    }
    return m;
  }, [items]);

  const overriddenCount = items.filter((i) => i.is_overridden).length;

  return (
    <div data-testid="sms-templates-panel">
      <div className="hah-card p-3 mb-4 flex items-center gap-3 border-l-4 border-l-[#0B7E8C]">
        <div className="flex-1 text-sm">
          <strong className="text-[#1F2937]">SMS Templates</strong>
          <span className="text-[#6B7280] ml-2">
            · {items.length} events · {overriddenCount} customised · placeholders shown per event
          </span>
        </div>
        <Button variant="outline" onClick={load} className="border-[#E5E7EB] h-8 text-xs" data-testid="sms-templates-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading && <div className="p-6 text-sm text-[#6B7280]">Loading…</div>}

      {!loading && Array.from(byCategory.entries()).map(([cat, list]) => {
        const c = CATEGORY_COLOR[cat] || { bg: "#F1F5F9", text: "#475569" };
        return (
          <div key={cat} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                style={{ background: c.bg, color: c.text }}
              >
                {cat}
              </span>
              <span className="text-xs text-[#6B7280]">{list.length} templates</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {list.map((t) => (
                <div
                  key={t.event_type}
                  className={`hah-card p-3.5 ${t.is_overridden ? "border-l-4 border-l-[#0B7E8C]" : ""}`}
                  data-testid={`sms-template-card-${t.event_type}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#1F2937]">{t.label}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">
                        {t.event_type} · → {t.recipient}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <SegmentBadge length={t.length} segments={t.segments} per_segment={t.per_segment} />
                      {t.is_overridden ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold bg-[#0B7E8C]/15 text-[#0B7E8C]">
                          Custom
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold bg-[#F1F5F9] text-[#475569]">
                          Default
                        </span>
                      )}
                    </div>
                  </div>

                  <pre className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-2 text-[11px] text-[#1F2937] whitespace-pre-wrap font-mono mt-2 max-h-24 overflow-y-auto">
                    {t.body}
                  </pre>

                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.placeholders.map((p) => (
                      <code key={p} className="text-[10px] bg-[#E0E7FF] text-[#3730A3] px-1.5 py-0.5 rounded font-mono">
                        {`{${p}}`}
                      </code>
                    ))}
                  </div>

                  {t.is_overridden && t.updated_by_name && (
                    <div className="text-[10px] text-[#6B7280] mt-2 border-t border-[#E5E7EB] pt-1.5">
                      Edited by <span className="font-semibold text-[#1F2937]">{t.updated_by_name}</span>
                      {t.updated_at && <> · {new Date(t.updated_at).toLocaleString()}</>}
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setEditing(t)}
                      className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-7 text-xs"
                      data-testid={`sms-template-edit-${t.event_type}`}
                    >
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <TemplateEditorDialog
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

/* ============================================================================
 *  Editor dialog
 * ==========================================================================*/
function TemplateEditorDialog({ template, onClose, onSaved }) {
  const [body, setBody] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [variables, setVariables] = useState({});

  useEffect(() => {
    if (!template) return;
    setBody(template.body || "");
    setChangeNote("");
    setShowHistory(false);
    // seed sample variables
    const sample = {};
    for (const p of template.placeholders || []) sample[p] = SAMPLE_VALUES[p] ?? `<${p}>`;
    setVariables(sample);
    // load history
    api.get(`/sms/templates/${template.event_type}`).then((r) => setHistory(r.data.history || [])).catch(() => {});
  }, [template]);

  // Debounced preview
  useEffect(() => {
    if (!template) return;
    const handle = setTimeout(async () => {
      try {
        const r = await api.post(`/sms/templates/${template.event_type}/preview`, { body, variables });
        setPreview(r.data);
      } catch (e) {
        setPreview({ ok: false, error: formatApiError(e) });
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [body, variables, template]);

  if (!template) return null;

  async function save() {
    if (!body.trim()) { toast.error("Body is required"); return; }
    setSaving(true);
    try {
      await api.put(`/sms/templates/${template.event_type}`, { body, change_note: changeNote || null });
      toast.success("Template saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  async function reset() {
    if (!window.confirm(`Reset "${template.label}" to the default body? This is logged in history.`)) return;
    setSaving(true);
    try {
      await api.post(`/sms/templates/${template.event_type}/reset`);
      toast.success("Reset to default");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  function insertPlaceholder(p) {
    setBody((b) => `${b}{${p}}`);
  }

  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sms-template-editor">
        <DialogHeader>
          <DialogTitle>
            Edit Template — {template.label}
            <span className="ml-2 text-xs font-mono text-[#6B7280]">{template.event_type}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-[#6B7280]">
            <strong>Recipient:</strong> {template.recipient} · <strong>Placeholders allowed:</strong>{" "}
            {template.placeholders.map((p, i) => (
              <span key={p}>
                <button
                  type="button"
                  onClick={() => insertPlaceholder(p)}
                  className="text-[#0B7E8C] hover:underline font-mono"
                  data-testid={`sms-template-insert-${p}`}
                >
                  {`{${p}}`}
                </button>
                {i < template.placeholders.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>

          <FormField label="Body">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="font-mono text-sm"
              data-testid="sms-template-body"
            />
          </FormField>

          {/* Sample variables for preview */}
          <details className="text-xs">
            <summary className="cursor-pointer text-[#0B7E8C] font-semibold">Sample variables (for preview)</summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {(template.placeholders || []).map((p) => (
                <FormField key={p} label={p}>
                  <Input
                    value={variables[p] ?? ""}
                    onChange={(e) => setVariables({ ...variables, [p]: e.target.value })}
                    data-testid={`sms-template-var-${p}`}
                  />
                </FormField>
              ))}
            </div>
          </details>

          {/* Live preview */}
          <div className="hah-card p-3 border-l-4" style={{ borderColor: preview?.ok ? "#10B981" : "#F59E0B" }}>
            <div className="flex items-center gap-2 mb-1.5">
              <Eye className="w-4 h-4 text-[#0B7E8C]" />
              <strong className="text-xs uppercase tracking-wider text-[#1F2937]">Live Preview</strong>
              {preview && (
                <SegmentBadge length={preview.length} segments={preview.segments} per_segment={preview.per_segment} />
              )}
            </div>
            <pre className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-2 text-[11px] whitespace-pre-wrap font-mono text-[#1F2937]" data-testid="sms-template-preview">
              {preview?.rendered ?? body}
            </pre>
            {preview?.issues?.length > 0 && (
              <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#92400E]">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{preview.issues.join(" · ")}</span>
              </div>
            )}
          </div>

          <FormField label="Change note (optional, recorded in history)">
            <Input
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="e.g. Friendlier tone for KSA rollout"
              data-testid="sms-template-note"
            />
          </FormField>

          {/* History */}
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            className="text-xs text-[#0B7E8C] flex items-center gap-1 font-semibold"
            data-testid="sms-template-toggle-history"
          >
            <History className="w-3.5 h-3.5" />
            {showHistory ? "Hide" : "Show"} change history ({history.length})
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showHistory ? "rotate-90" : ""}`} />
          </button>
          {showHistory && (
            <div className="border border-[#E5E7EB] rounded overflow-hidden">
              {history.length === 0 ? (
                <div className="p-3 text-xs text-[#6B7280] text-center">No changes yet</div>
              ) : (
                <div className="overflow-x-auto"><table className="w-full text-xs">
                  <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Action</th>
                      <th className="text-left p-2">By</th>
                      <th className="text-left p-2">Change Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className="border-t border-[#E5E7EB]">
                        <td className="p-2 text-[#6B7280]">{new Date(h.created_at).toLocaleString()}</td>
                        <td className="p-2"><span className="font-bold text-[#1F2937]">{h.action}</span></td>
                        <td className="p-2 text-[#1F2937]">{h.actor_name}<br/><span className="text-[10px] text-[#6B7280]">{h.actor_role}</span></td>
                        <td className="p-2 italic text-[#475569]">{h.change_note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {template.is_overridden && (
            <Button variant="outline" onClick={reset} disabled={saving} className="border-[#E5E7EB] text-[#991B1B] mr-auto" data-testid="sms-template-reset">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to default
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !!(preview?.issues?.length)}
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            data-testid="sms-template-save"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SAMPLE_VALUES = {
  patient_name: "Khulood Al-Maliki",
  mrn: "HAH-2026-0001",
  vital_type: "SpO2",
  value: "85%",
  severity: "CRITICAL",
  record_id: "HL-2026-0042",
  reason: "Severe chest pain",
  when: "Tomorrow 09:30 KSA",
  status: "Approved for home care",
  detail: "Visit being scheduled.",
  eta: "~20 minutes",
  email: "user@hah.health",
  name: "Dr. Reem Al-Omari",
  license_number: "MD-12345",
  days_left: 14,
  expiry_date: "2026-07-01",
};
